// Notification rules: which channels to use for an alert, when SMS kicks in, quota levels.
// Delivery state keys match the copy library: message_queued / sending / sent / delivered / failed.
import { isWindowOpen, type Channel, type TicketState } from './ticket';

export type DeliveryState = 'queued' | 'sending' | 'sent' | 'delivered' | 'read' | 'failed';
export type AlertChannel = 'whatsapp' | 'web' | 'push';
export type QuotaLevel = 'ok' | 'warning' | 'exhausted';

export const SMS_FALLBACK_MS = 20_000;
export const QUOTA_WARNING_RATIO = 0.8;

/** Channels to fire on "ready", in order of preference. The web ring is always included: an open page rings for free. */
export function planReadyAlert(
  t: Pick<TicketState, 'channel' | 'phone' | 'waWindowUntil'>,
  opts: { now: number; pushSubscribed: boolean },
): AlertChannel[] {
  const channels: AlertChannel[] = [];
  if (t.channel === 'whatsapp' && t.phone && isWindowOpen(t, opts.now)) channels.push('whatsapp');
  if (opts.pushSubscribed) channels.push('push');
  channels.push('web');
  return channels;
}

export type SmsReason = 'no_phone' | 'blocked' | 'delivered' | 'too_early' | 'quota_exhausted' | 'fallback';

/** The 20-second rule: SMS only when WhatsApp has no delivered status after 20 s (or failed outright). */
export function smsFallbackDecision(a: {
  phone: string | null;
  blocked: boolean;
  whatsappState: DeliveryState | 'none';
  elapsedMs: number;
  smsUsed: number;
  smsQuota: number;
}): { send: boolean; reason: SmsReason } {
  if (!a.phone) return { send: false, reason: 'no_phone' };
  if (a.blocked) return { send: false, reason: 'blocked' };
  if (a.whatsappState === 'delivered' || a.whatsappState === 'read') return { send: false, reason: 'delivered' };
  if (a.whatsappState !== 'failed' && a.elapsedMs < SMS_FALLBACK_MS) return { send: false, reason: 'too_early' };
  if (a.smsUsed >= a.smsQuota) return { send: false, reason: 'quota_exhausted' };
  return { send: true, reason: 'fallback' };
}

/**
 * Delivery of the "ready" alert on the web channel. The page's socket is the carrier: a ticket whose page is
 * attached when staff tap ready got its ring ("sent"); one whose page is gone has nobody left to reach, so staff
 * must call the number out loud.
 */
export function webReadyDelivery(pageConnected: boolean): { state: Extract<DeliveryState, 'sent' | 'failed'>; needsCall: boolean } {
  return pageConnected ? { state: 'sent', needsCall: false } : { state: 'failed', needsCall: true };
}

/**
 * Ready on a ticket nobody registered (a "Nouveau numéro" from the staff screen, a POS ticket never scanned, a guest
 * who sent STOP): there is nothing to send, so no delivery is attempted and the order is flagged "à appeler" in the
 * same update that makes it ready. The board, the chime and the call-out are the alert. A ticket with a channel is
 * flagged only once its alert has failed (`webReadyDelivery`, the SMS fallback).
 */
export function callOutAtReady(t: Pick<TicketState, 'channel'>): boolean {
  return t.channel === 'none';
}

export function quotaLevel(used: number, quota: number): QuotaLevel {
  if (quota <= 0 || used >= quota) return 'exhausted';
  if (used >= Math.ceil(quota * QUOTA_WARNING_RATIO)) return 'warning';
  return 'ok';
}

/** Map a provider status word to our delivery state. */
export function deliveryStateFromProvider(status: string): DeliveryState {
  switch (status) {
    case 'sent':
      return 'sent';
    case 'delivered':
      return 'delivered';
    case 'read':
      return 'read';
    case 'failed':
    case 'undelivered':
      return 'failed';
    default:
      return 'sending';
  }
}

// ---- SMS counter period ----

/** Month of the venue's SMS counter (UTC `YYYY-MM`); `venues.sms_period` holds the month `sms_used` belongs to. */
export function smsPeriod(now: number): string {
  const d = new Date(now);
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

/** A counter from another month is stale: the new month starts at zero even before the first SMS resets the row. */
export function smsUsedInPeriod(v: { smsUsed: number; smsPeriod: string }, period: string): number {
  return v.smsPeriod === period ? v.smsUsed : 0;
}

/** The staff header shows the counter once half the quota is used (and whenever the quota is exhausted). */
export const SMS_QUOTA_SHOW_RATIO = 0.5;

export function showSmsQuota(used: number, quota: number): boolean {
  if (quota <= 0) return used > 0;
  return used >= quota * SMS_QUOTA_SHOW_RATIO;
}

// ---- the alert delivery shown to staff ----

/**
 * The last alert attempt for a ready ticket: which channel carried it and how far it got. Separate from the order
 * state and from the registered channel: a WhatsApp guest whose message was not delivered gets an SMS, and the
 * staff row then shows the SMS state.
 */
export interface AlertDelivery {
  channel: Channel;
  state: DeliveryState;
}

/** Order of the states as a provider reports them; a lower one arriving later is a late or duplicate report. */
const DELIVERY_RANK: Record<DeliveryState, number> = { queued: 0, sending: 1, sent: 2, failed: 3, delivered: 4, read: 5 };

/**
 * Apply a provider report on top of what staff currently see. A report for another channel always wins (it is
 * the latest attempt); on the same channel the state never goes backwards, so a `sent` arriving after
 * `delivered` is ignored, while a `delivered` arriving after a timeout `failed` replaces it.
 * A new send (ready, resend, SMS fallback) is not a report: callers set it directly.
 */
export function advanceDelivery(prev: AlertDelivery | null, next: AlertDelivery): AlertDelivery {
  if (!prev || prev.channel !== next.channel) return next;
  return DELIVERY_RANK[next.state] >= DELIVERY_RANK[prev.state] ? next : prev;
}
